import {
  Parameter,
  ParameterOption,
  ParameterRange,
  ParameterType,
  RangeValue,
  MarkdownVariant,
  XYPadAxes,
  XYZPadAxes,
  GridConfig,
  StackConfig,
  PolygonPadConfig,
} from '@shared/types';
import { scadStringToHex } from '../colorUtils';
import { generateDisplayName } from './formatting';
import { joinMultiLineAssignments } from './updater';
import type { CodeSnapshot, PipCameraConfig, MeshpitAction } from './types';

// Per-type parsers (one file per ParameterType, mirroring src/components/parameter/)
import { parseStepperType } from './parsers/stepper';
import { parseGridType } from './parsers/grid';
import { parseStackType } from './parsers/stack';
import { parseMaterialType } from './parsers/material';
import {
  parsePolygonPad,
  parsePolygonPadType,
} from './parsers/polygonPad';
import { parseRangeType } from './parsers/range';
import { parseColorFromArray } from './parsers/color';
import { parseXYPadType } from './parsers/xyPad';
import { parseXYZPadType } from './parsers/xyzPad';
import { parseNumberType } from './parsers/number';
import { parseBooleanType } from './parsers/boolean';
import { parseStringType } from './parsers/string';
import { parseCodeType } from './parsers/code';

// Re-export parsePolygonPad to preserve the existing public API
export { parsePolygonPad };

export const parseParameters = (code: string): Parameter[] => {
  const params: Parameter[] = [];

  const lines = joinMultiLineAssignments(code).split('\n');
  // Updated regex to allow special variables starting with $ (like $fn)
  const regex = /^(\s*)(\$?[a-zA-Z_][a-zA-Z0-9_]*)\s*=\s*([^;]+);(.*)$/;
  let currentGroup = 'General';
  let currentTab: string | undefined = undefined;
  let currentGroupVisibilityExpr: string | undefined = undefined;
  let currentGroupLocked = false;
  let lastComment = '';
  let nextIsUpload = false;
  let nextIsSvgCheckboxGroup = false;
  let nextIsSvgRadioGroup = false;
  let stopParsing = false;
  // Markdown accumulation state
  let collectingMarkdown = false;
  let markdownLines: string[] = [];
  let markdownCounter = 0;
  let currentMarkdownVariant: MarkdownVariant | undefined = undefined;
  let currentMarkdownVisibilityExpr: string | undefined = undefined;
  // camera_pip accumulation state (handles multi-line calls)
  let collectingPip = false;
  let pipLines: string[] = [];
  let pipCounter = 0;

  /** Flush any accumulated camera_pip lines as a synthetic camera_pip parameter. */
  const flushPip = () => {
    if (!collectingPip || pipLines.length === 0) {
      collectingPip = false;
      pipLines = [];
      return;
    }
    const pipText = pipLines.join(' ');
    const pipConfig = parseCameraPipCall(pipText);
    if (pipConfig) {
      const jsonValue = JSON.stringify(pipConfig);
      params.push({
        name: `__camera_pip_${pipCounter++}`,
        displayName: '',
        value: jsonValue,
        defaultValue: jsonValue,
        type: 'camera_pip',
        group: currentGroup,
        tab: currentTab,
        groupVisibilityExpr: currentGroupVisibilityExpr,
      });
    }
    collectingPip = false;
    pipLines = [];
  };

  /** Flush any accumulated markdown lines as a synthetic markdown parameter. */
  const flushMarkdown = () => {
    if (!collectingMarkdown || markdownLines.length === 0) {
      collectingMarkdown = false;
      markdownLines = [];
      currentMarkdownVariant = undefined;
      return;
    }
    const markdownContent = markdownLines.join('\n');
    params.push({
      name: `__markdown_${markdownCounter++}`,
      displayName: '',
      value: markdownContent,
      defaultValue: markdownContent,
      type: 'markdown',
      group: currentGroup,
      tab: currentTab,
      markdownVariant: currentMarkdownVariant,
      groupVisibilityExpr: currentGroupVisibilityExpr,
      visibilityExpr: currentMarkdownVisibilityExpr,
    });
    collectingMarkdown = false;
    markdownLines = [];
    currentMarkdownVariant = undefined;
    currentMarkdownVisibilityExpr = undefined;
  };

  lines.forEach((line) => {
    if (stopParsing) return;

    const trimmedLine = line.trim();

    if (trimmedLine.includes('// --- parameters end ---')) {
      flushPip();
      flushMarkdown();
      stopParsing = true;
      return;
    }

    const groupMatch = line.match(
      /\/\*\s*\[([^\]]+)\]\s*(@lock\s*)?\s*(?:\{([^}]*)\})?\s*\*\//,
    );
    if (groupMatch) {
      flushPip();
      flushMarkdown();
      // A trailing ^ marks the group as collapsed by default.
      // The ^ is kept in the group name so ParameterSection can detect it;
      // the UI strips it before display.
      //
      // Support `/* [Tab Name: Group Name] */` syntax.
      // If the label contains a colon, the part before the first colon is the
      // tab name and the remainder is the group name.
      const rawLabel = groupMatch[1].trim();
      const colonIndex = rawLabel.indexOf(':');
      if (colonIndex !== -1) {
        currentTab = rawLabel.slice(0, colonIndex).trim();
        currentGroup = rawLabel.slice(colonIndex + 1).trim();
      } else {
        currentTab = undefined;
        currentGroup = rawLabel;
      }
      // @lock annotation locks all parameters in this group by default.
      currentGroupLocked = !!groupMatch[2];
      // Optional {expression} after the group name controls group visibility.
      currentGroupVisibilityExpr = groupMatch[3]?.trim() || undefined;
      lastComment = '';
      nextIsUpload = false;
      return;
    }

    if (trimmedLine.startsWith('//') && !line.match(regex)) {
      if (trimmedLine === '// upload') {
        flushMarkdown();
        nextIsUpload = true;
      } else if (trimmedLine === '// svgcheckboxgroup') {
        flushMarkdown();
        nextIsSvgCheckboxGroup = true;
      } else if (trimmedLine === '// svgradiogroup') {
        flushMarkdown();
        nextIsSvgRadioGroup = true;
      } else if (
        trimmedLine === '// markdown' ||
        trimmedLine.startsWith('// markdown:')
      ) {
        // Start a new markdown block; clear any pending lastComment so it
        // doesn't leak into the next real parameter's tooltip.
        lastComment = '';
        collectingMarkdown = true;
        markdownLines = [];
        let variantSuffix = trimmedLine.startsWith('// markdown:')
          ? trimmedLine.slice('// markdown:'.length).trim()
          : '';
        // Extract optional visibility expression {expr} from the suffix,
        // e.g. "// markdown:error  {wall < 1.5}" → variant="error", expr="wall < 1.5"
        const mdVisibilityMatch = variantSuffix.match(/\{([^}]*)\}\s*$/);
        currentMarkdownVisibilityExpr =
          mdVisibilityMatch?.[1]?.trim() || undefined;
        if (mdVisibilityMatch) {
          variantSuffix = variantSuffix
            .slice(0, mdVisibilityMatch.index)
            .trim();
        }
        const VALID_VARIANTS: MarkdownVariant[] = [
          'idea',
          'warning',
          'note',
          'tip',
          'error',
        ];
        currentMarkdownVariant = VALID_VARIANTS.includes(
          variantSuffix as MarkdownVariant,
        )
          ? (variantSuffix as MarkdownVariant)
          : undefined;
      } else if (collectingMarkdown) {
        // Strip the leading "// " or "//" and accumulate
        markdownLines.push(trimmedLine.replace(/^\/\/\s?/, ''));
      } else {
        lastComment = trimmedLine;
      }
      return;
    }

    // Any non-comment line ends markdown collection
    flushMarkdown();

    // Detect start/continuation of a camera_pip(...) call (may be multi-line)
    if (collectingPip) {
      pipLines.push(trimmedLine);
      // Check if this line closes the call
      if (trimmedLine.includes(');')) {
        flushPip();
      }
      return;
    }
    if (trimmedLine.startsWith('camera_pip(')) {
      lastComment = '';
      collectingPip = true;
      pipLines = [trimmedLine];
      // Single-line call?
      if (trimmedLine.includes(');')) {
        flushPip();
      }
      return;
    }

    const match = line.match(regex);
    if (match) {
      const name = match[2];
      const valueStr = match[3].trim();
      let comment = match[4].trim().replace(/^\/\/\s*/, '');

      // Extract visibility expression {expr} from the comment before any
      // other parsing, so option/range hints are not confused with it.
      const visibilityMatch = comment.match(/\{([^}]*)\}\s*$/);
      const visibilityExpr = visibilityMatch?.[1]?.trim() || undefined;
      if (visibilityMatch) {
        comment = comment.slice(0, visibilityMatch.index).trim();
      }

      // Extract @lock annotation — locks this parameter by default.
      const hasLockAnnotation = /@lock\b/.test(comment);
      if (hasLockAnnotation) {
        comment = comment.replace(/@lock\b/, '').trim();
      }

      // Extract @pubsub annotation — connects this parameter to the pub/sub system.
      const hasPubSubAnnotation = /@pubsub\b/.test(comment);
      if (hasPubSubAnnotation) {
        comment = comment.replace(/@pubsub\b/, '').trim();
      }

      // Capture the preceding comment as tooltip (human-readable description)
      const tooltipComment = lastComment;

      // If no inline comment, use preceding comment for parsing range/options
      if (!comment && lastComment) {
        comment = lastComment;
      }
      lastComment = '';

      let value: number | string | boolean | number[] | string[] | RangeValue =
        valueStr;
      let type: ParameterType = 'string';
      let range: ParameterRange | undefined = undefined;
      let options: ParameterOption[] | undefined = undefined;
      let vectorLabels: string[] | undefined = undefined;
      let svgFile: string | undefined = undefined;
      let stepperLoop: boolean | undefined = undefined;
      let multiline: boolean | undefined = undefined;
      let xyPadAxes: XYPadAxes | undefined = undefined;
      let xyzPadAxes: XYZPadAxes | undefined = undefined;
      let gridConfig: GridConfig | undefined = undefined;
      let stackConfig: StackConfig | undefined = undefined;
      let polygonPadConfig: PolygonPadConfig | undefined = undefined;
      let unit: string | undefined = undefined;

      // -----------------------------------------------------------------------
      // Stepper detection (checked first so it works for both number and
      // string default values). Strips the <...> block from the comment.
      // -----------------------------------------------------------------------
      const stepperResult = parseStepperType(valueStr, comment);
      if (stepperResult) {
        type = stepperResult.type;
        value = stepperResult.value;
        options = stepperResult.options;
        stepperLoop = stepperResult.stepperLoop;
        comment = stepperResult.comment;
      }

      // -----------------------------------------------------------------------
      // Grid detection: // grid: CxR [opt1, opt2, ...]
      // -----------------------------------------------------------------------
      if (type !== 'stepper') {
        const gridResult = parseGridType(valueStr, comment);
        if (gridResult) {
          type = gridResult.type;
          value = gridResult.value;
          gridConfig = gridResult.gridConfig;
        }
      }

      // -----------------------------------------------------------------------
      // Stack detection: // stack: [min:max] [opt1, opt2, ...]
      // -----------------------------------------------------------------------
      if (type !== 'stepper' && type !== 'grid') {
        const stackResult = parseStackType(valueStr, comment);
        if (stackResult) {
          type = stackResult.type;
          value = stackResult.value;
          stackConfig = stackResult.stackConfig;
        }
      }

      // -----------------------------------------------------------------------
      // Remaining value-based type detection waterfall
      // -----------------------------------------------------------------------
      if (type !== 'stepper' && type !== 'grid' && type !== 'stack') {
        // Detect material_def(...) function calls
        const materialResult = parseMaterialType(valueStr);
        if (materialResult) {
          type = materialResult.type;
          value = materialResult.value;
        } else if (valueStr.startsWith('[') && valueStr.endsWith(']')) {
          const content = valueStr.slice(1, -1).trim();

          // -------------------------------------------------------
          // Polygon pad detection: nested array of 2-element numeric
          // sub-arrays, e.g. [[0,0], [10,0], [10,10], [0,10]]
          // -------------------------------------------------------
          if (content.startsWith('[')) {
            const polygonResult = parsePolygonPadType(valueStr, comment);
            if (polygonResult) {
              type = polygonResult.type;
              value = polygonResult.value;
              polygonPadConfig = polygonResult.polygonPadConfig;
            }
          }

          // Check for range syntax (colons, no commas)
          if (type !== 'polygonpad') {
            const rangeResult = parseRangeType(valueStr);
            if (rangeResult) {
              type = rangeResult.type;
              value = rangeResult.value;
            }
          }

          if (type !== 'polygonpad' && type !== 'range') {
            const elements = content.split(',').map((s) => s.trim());
            const numElements = elements.map(Number);

            if (!numElements.some(isNaN)) {
              // Color from numeric array (3- or 4-element + "color" hint)
              const colorResult = parseColorFromArray(
                numElements,
                comment,
                name,
              );
              if (colorResult) {
                type = colorResult.type;
                value = colorResult.value;
              } else {
                type = 'number[]';
                value = numElements;

                // XY pad detection (2-element + dual range comment)
                const xyResult = parseXYPadType(numElements, comment);
                if (xyResult) {
                  type = xyResult.type;
                  xyPadAxes = xyResult.xyPadAxes;
                }

                // XYZ pad detection (3-element + triple range comment)
                const xyzResult = parseXYZPadType(numElements, comment);
                if (xyzResult) {
                  type = xyzResult.type;
                  xyzPadAxes = xyzResult.xyzPadAxes;
                }

                // Check for vector labels in comment like [x, y, z]
                // (only when not promoted to xypad or xyzpad)
                if (type !== 'xypad' && type !== 'xyzpad' && comment) {
                  const vectorLabelsMatch = comment.match(
                    /\[([a-zA-Z0-9_\s,]+)\]/,
                  );
                  if (vectorLabelsMatch) {
                    const labels = vectorLabelsMatch[1]
                      .split(',')
                      .map((s) => s.trim());
                    // Verify these are not numbers (which would be options) or ranges
                    const isLabels = labels.every(
                      (l) => isNaN(Number(l)) && !l.includes(':'),
                    );
                    if (isLabels && labels.length === elements.length) {
                      vectorLabels = labels;
                    }
                  }
                }
              }
            } else {
              type = 'string';
            }
          }
        } else {
          // Non-array, non-material value — try number, boolean, string in order
          const numberResult = parseNumberType(valueStr, comment);
          if (numberResult) {
            type = numberResult.type;
            value = numberResult.value;
            if (numberResult.range) range = numberResult.range;
            if (numberResult.options) options = numberResult.options;
            if (numberResult.unit !== undefined) unit = numberResult.unit;
          } else {
            const booleanResult = parseBooleanType(valueStr);
            if (booleanResult) {
              type = booleanResult.type;
              value = booleanResult.value;
            } else {
              const codeResult = parseCodeType(valueStr, comment);
              if (codeResult) {
                type = codeResult.type;
                value = codeResult.value;
                if (codeResult.options) options = codeResult.options;
                // Strip [code] from the comment so downstream parsers don't
                // misinterpret it as an options list
                comment = comment.replace(/\[code\]/i, '').trim();
              } else {
                const stringResult = parseStringType(
                  valueStr,
                  comment,
                  name,
                  nextIsUpload,
                  nextIsSvgCheckboxGroup,
                  nextIsSvgRadioGroup,
                );
                if (stringResult) {
                  type = stringResult.type;
                  value = stringResult.value;
                  if (stringResult.options) options = stringResult.options;
                  if (stringResult.svgFile) svgFile = stringResult.svgFile;
                  if (stringResult.multiline) multiline = stringResult.multiline;
                }
              }
            }
          }
        }
      } // end waterfall

      // -----------------------------------------------------------------------
      // Range parsing for number[] / range types (number type already parsed
      // ranges inside parseNumberType). Bounds may be literal numbers or
      // $-prefixed parameter references.
      // -----------------------------------------------------------------------
      if ((type === 'number[]' || type === 'range') && comment) {
        const NR = '(-?\\d+(?:\\.\\d+)?|\\$[a-zA-Z_][a-zA-Z0-9_]*)';
        const parseBound = (raw: string): number | string =>
          raw.startsWith('$') ? raw : Number(raw);

        const minMatch = comment.match(new RegExp(`min[:=]\\s*${NR}`, 'i'));
        const maxMatch = comment.match(new RegExp(`max[:=]\\s*${NR}`, 'i'));
        const stepMatch = comment.match(new RegExp(`step[:=]\\s*${NR}`, 'i'));

        if (minMatch || maxMatch || stepMatch) {
          range = range || {};
          if (minMatch) range.min = parseBound(minMatch[1]);
          if (maxMatch) range.max = parseBound(maxMatch[1]);
          if (stepMatch) range.step = parseBound(stepMatch[1]);
        }
      }

      // Unit label — applies to number, angle, and number[] types
      if (
        unit === undefined &&
        (type === 'number' || type === 'angle' || type === 'number[]') &&
        comment
      ) {
        const unitMatch = comment.match(/\bunit[:=]\s*([^\s,\]]*)/i);
        if (unitMatch) {
          unit = unitMatch[1].trim();
        }
      }

      nextIsUpload = false;
      nextIsSvgCheckboxGroup = false;
      nextIsSvgRadioGroup = false;

      if (!params.find((p) => p.name === name)) {
        // Extract clean tooltip from preceding comment (remove // prefix)
        const tooltip = tooltipComment
          ? tooltipComment.replace(/^\/\/\s*/, '').trim()
          : undefined;
        // Only use tooltip if it's a meaningful description (not just range/option hints)
        const isDescriptiveTooltip =
          tooltip && !tooltip.match(/^(\[|min[:=]|max[:=]|step[:=]|color$)/i);

        params.push({
          name,
          displayName: name === '$fn' ? '$fn' : generateDisplayName(name),
          value,
          defaultValue: value,
          type,
          range,
          options,
          vectorLabels,
          svgFile,
          stepperLoop,
          xyPadAxes,
          xyzPadAxes,
          gridConfig,
          stackConfig,
          polygonPadConfig,
          group: currentGroup,
          tab: currentTab,
          description: comment.replace(/^\/\/\s*/, ''),
          tooltip: isDescriptiveTooltip ? tooltip : undefined,
          visibilityExpr,
          groupVisibilityExpr: currentGroupVisibilityExpr,
          locked: hasLockAnnotation || currentGroupLocked || undefined,
          pubsub: hasPubSubAnnotation || undefined,
          multiline: multiline || undefined,
          unit: unit !== undefined ? unit : undefined,
        });
      }
    } else {
      if (trimmedLine !== '') {
        lastComment = '';
        nextIsUpload = false;
        nextIsSvgCheckboxGroup = false;
        nextIsSvgRadioGroup = false;
      }
    }
  });

  // Flush any trailing pip/markdown blocks
  flushPip();
  flushMarkdown();

  // Resolve line numbers against the original (un-joined) source code.
  // joinMultiLineAssignments can shift line indices, so we scan the raw `code`
  // string independently to find the 1-based line number of each parameter's
  // declaration line.
  const rawLines = code.split('\n');
  for (const param of params) {
    if (param.name.startsWith('__')) continue; // synthetic params (markdown, pip)
    // Match e.g. `  width = ` or `$fn = ` at the start of a line
    const escapedName = param.name.replace('$', '\\$');
    const declRe = new RegExp(`^\\s*${escapedName}\\s*=`);
    const lineIdx = rawLines.findIndex((l) => declRe.test(l));
    if (lineIdx !== -1) {
      param.lineNumber = lineIdx + 1;
    }
  }

  // Ensure $fn is always at the top if it exists
  const fnIndex = params.findIndex((p) => p.name === '$fn');
  if (fnIndex !== -1) {
    const fnParam = params[fnIndex];
    // Force $fn to be in the General group so it appears at the top (ungrouped),
    // and clear any tab so it always appears on the home tab.
    fnParam.group = 'General';
    fnParam.tab = undefined;
    // Remove from current position
    params.splice(fnIndex, 1);
    // Add to the beginning
    params.unshift(fnParam);
  }

  return params;
};

// ---------------------------------------------------------------------------
// Snapshot definitions parsed from SCAD code
// ---------------------------------------------------------------------------

/**
 * Parses pre-defined named snapshots from a `/* [Snapshots] ... *\/` block
 * in the SCAD source. Each non-empty line inside the block must follow the
 * format:
 *
 *   Snapshot Name: param1=value1, param2=value2
 *
 * Values are raw strings (quoted for strings, bare for numbers/booleans).
 * Strings may be quoted with either single or double quotes.
 *
 * Example:
 *   /* [Snapshots]
 *   Small Box: size=10, color="#FF0000"
 *   Large Box: size=50, color="#0000FF"
 *   *\/
 */
export function parseSnapshots(code: string): CodeSnapshot[] {
  const snapshots: CodeSnapshot[] = [];

  // Match the first /* [Snapshots] ... */ block (non-greedy, DOTALL via [\s\S])
  const blockMatch = code.match(/\/\*\s*\[Snapshots\]([\s\S]*?)\*\//i);
  if (!blockMatch) return snapshots;

  const blockContent = blockMatch[1];
  const lines = blockContent.split('\n');

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('//') || trimmed.startsWith('*')) {
      continue;
    }

    // Expect "Name: key=value, key=value, ..."
    const colonIdx = trimmed.indexOf(':');
    if (colonIdx === -1) continue;

    const name = trimmed.slice(0, colonIdx).trim();
    if (!name) continue;

    const valuesPart = trimmed.slice(colonIdx + 1).trim();
    if (!valuesPart) continue;

    const values: Record<string, string> = {};

    // Split by comma, but respect quoted strings so commas inside quotes
    // don't split entries (e.g. color="#FF0000,00" is not a concern in
    // practice, but we handle it for robustness).
    const entries = splitSnapshotValues(valuesPart);
    for (const entry of entries) {
      const eqIdx = entry.indexOf('=');
      if (eqIdx === -1) continue;
      const key = entry.slice(0, eqIdx).trim();
      const rawVal = entry.slice(eqIdx + 1).trim();
      if (key) {
        values[key] = rawVal;
      }
    }

    if (Object.keys(values).length > 0) {
      snapshots.push({ name, values });
    }
  }

  return snapshots;
}

// ---------------------------------------------------------------------------
// Snapshot serialization
// ---------------------------------------------------------------------------

/**
 * Serializes a single parameter's current value into the raw string format
 * used inside a `/* [Snapshots] ... *\/` block.
 *
 * Returns `null` for synthetic/internal parameters (markdown, camera_pip)
 * that should not appear in snapshot output.
 */
function serializeSnapshotValue(param: Parameter): string | null {
  // Skip internal synthetic parameters
  if (
    param.type === 'markdown' ||
    param.type === 'camera_pip' ||
    param.name.startsWith('__')
  ) {
    return null;
  }

  const v = param.value;

  switch (param.type) {
    case 'string':
    case 'file':
    case 'font':
      return `"${String(v).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;

    case 'boolean':
      return String(v);

    case 'number':
    case undefined: // untyped number params
      return String(v);

    case 'number[]':
      return `[${(v as number[]).join(', ')}]`;

    case 'boolean[]':
      return `[${(v as boolean[]).join(', ')}]`;

    case 'string[]':
      return `[${(v as string[])
        .map((s) => `"${s.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`)
        .join(', ')}]`;

    case 'range': {
      const r = v as { start: number; end: number; step: number | null };
      const step = r.step !== null ? `:${r.step}` : '';
      return `[${r.start}${step}:${r.end}]`;
    }

    case 'color': {
      // Normalize to a hex string (the snapshot format uses quoted hex strings)
      if (typeof v === 'string') {
        // Already a hex or named color string — quote it
        const hex = scadStringToHex(v);
        return hex ? `"${hex}"` : `"${v}"`;
      }
      // Float array [r, g, b] or [r, g, b, a] — convert back to hex
      if (Array.isArray(v) && v.length >= 3) {
        const toHex = (n: number) =>
          Math.round(Math.min(1, Math.max(0, n)) * 255)
            .toString(16)
            .padStart(2, '0');
        const arr = v as number[];
        return `"#${toHex(arr[0])}${toHex(arr[1])}${toHex(arr[2])}"`;
      }
      return `"${String(v)}"`;
    }

    case 'material':
      // Material arrays are large; serialize as the raw array
      return `[${(v as number[]).join(', ')}]`;

    default:
      return String(v);
  }
}

/**
 * Converts the current parameter values into a single snapshot line suitable
 * for pasting inside a `/* [Snapshots] ... *\/` block:
 *
 *   My Snapshot: width=50, color="#FF0000", style="rounded"
 *
 * Synthetic parameters (markdown blocks, camera PIPs) are excluded.
 */
export function serializeSnapshotLine(
  name: string,
  parameters: Parameter[],
): string {
  const parts: string[] = [];
  for (const param of parameters) {
    const serialized = serializeSnapshotValue(param);
    if (serialized !== null) {
      parts.push(`${param.name}=${serialized}`);
    }
  }
  return `/* [Snapshots]\n${name}: ${parts.join(', ')}\n*/`;
}

// ---------------------------------------------------------------------------
// camera_pip() parameter panel PiP parsing
// ---------------------------------------------------------------------------

/**
 * Parses the argument text of a single camera_pip(...) call (with the outer
 * `camera_pip(` and closing `);` already stripped or still present — we
 * handle both) and returns a PipCameraConfig, or null if required fields
 * are missing or parsing fails.
 *
 * Only calls that omit both `x` and `y` (or set them to the special sentinel
 * value undef/omitted) are treated as parameter-panel PiPs. If either `x` or
 * `y` is present in the call, this returns null (it's a viewer overlay PiP).
 */
export function parseCameraPipCall(callText: string): PipCameraConfig | null {
  // If x= or y= are explicitly set, this is a viewer-overlay PiP — skip it.
  if (/\bx\s*=/.test(callText) || /\by\s*=/.test(callText)) return null;

  // Helper: extract a named vector argument [a, b, c]
  const vecArg = (name: string): [number, number, number] | null => {
    const m = callText.match(
      new RegExp(
        `\\b${name}\\s*=\\s*\\[\\s*([\\-\\d.e]+)\\s*,\\s*([\\-\\d.e]+)\\s*,\\s*([\\-\\d.e]+)\\s*\\]`,
      ),
    );
    if (!m) return null;
    return [parseFloat(m[1]), parseFloat(m[2]), parseFloat(m[3])];
  };

  // Helper: extract a named scalar number argument
  const numArg = (name: string, fallback: number): number => {
    const m = callText.match(new RegExp(`\\b${name}\\s*=\\s*([\\-\\d.e]+)`));
    return m ? parseFloat(m[1]) : fallback;
  };

  // Helper: extract a named quoted-string argument
  const strArg = (name: string, fallback: string): string => {
    const m = callText.match(new RegExp(`\\b${name}\\s*=\\s*"([^"]*)"`));
    return m ? m[1] : fallback;
  };

  const position = vecArg('position') ?? [50, 50, 50];
  const target = vecArg('target') ?? [0, 0, 0];
  const typeStr = strArg('type', 'perspective');
  const fov = numArg('fov', 45);
  const ar = strArg('ar', '4:3');
  const label = strArg('label', '');
  const title = strArg('title', '');
  const caption = strArg('caption', '');
  const modeStr = strArg('mode', 'default');
  const mode: PipCameraConfig['mode'] =
    modeStr === 'wireframe'
      ? 'wireframe'
      : modeStr === 'grayscale'
        ? 'grayscale'
        : 'default';

  return {
    position: position as [number, number, number],
    target: target as [number, number, number],
    type: typeStr === 'orthographic' ? 'orthographic' : 'perspective',
    fov,
    ar,
    mode,
    label,
    title,
    caption,
  };
}

// ---------------------------------------------------------------------------
// meshpit: action link parser
// ---------------------------------------------------------------------------

/**
 * Parses a `meshpit:` href and returns the corresponding action.
 * Returns `{ type: 'unknown' }` for any unrecognised format.
 *
 * @example
 *   parseMeshpitHref('meshpit:snapshot:rounded')
 *   // → { type: 'snapshot', id: 'rounded' }
 *
 *   parseMeshpitHref('meshpit:button_style:square')
 *   // → { type: 'parameter', name: 'button_style', value: 'square' }
 */
export function parseMeshpitHref(href: string): MeshpitAction {
  if (!href.startsWith('meshpit:')) return { type: 'unknown' };

  // Strip the protocol prefix and split on the first two colons only,
  // allowing values that themselves contain colons (e.g. hex colours).
  const rest = href.slice('meshpit:'.length);
  const firstColon = rest.indexOf(':');
  if (firstColon === -1) return { type: 'unknown' };

  const segment1 = rest.slice(0, firstColon);
  const segment2 = rest.slice(firstColon + 1);

  if (!segment1 || !segment2) return { type: 'unknown' };

  if (segment1 === 'snapshot') {
    return { type: 'snapshot', id: segment2 };
  }

  // Any other first segment is treated as a parameter name
  return { type: 'parameter', name: segment1, value: segment2 };
}

// ---------------------------------------------------------------------------
// Cheatsheet / tester helpers

/**
 * Parse a single SCAD parameter line (e.g. `height = 42; // [5:200]`) and
 * return the resulting Parameter, or null if the line doesn't match.
 *
 * This is a thin wrapper around parseParameters() — it reuses all existing
 * parsing logic with zero duplication.
 */
export function parseSingleParameter(line: string): Parameter | null {
  const results = parseParameters(line.trim() + '\n');
  return results.length > 0 ? results[0] : null;
}

// identifyVariant is re-exported from ./parameters/formatting

// ---------------------------------------------------------------------------

function splitSnapshotValues(input: string): string[] {
  const result: string[] = [];
  let current = '';
  let inString = false;
  let quoteChar = '';

  for (let i = 0; i < input.length; i++) {
    const ch = input[i];
    if (inString) {
      current += ch;
      if (ch === quoteChar) inString = false;
    } else if (ch === '"' || ch === "'") {
      inString = true;
      quoteChar = ch;
      current += ch;
    } else if (ch === ',') {
      result.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  if (current.trim()) result.push(current.trim());
  return result;
}
